import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fetchAttachmentBlob } from '@apis/monitor';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Fetches an attachment as a Blob (cached for 24h via TanStack Query) and
 * exposes it as an object URL suitable for `<img src>` / `<a href>`.
 *
 * The create/revoke pair MUST live in the same effect so the URL's lifetime
 * is bound to a single mount cycle. A `useMemo`-based variant tripped on
 * React 18 Strict Mode's double-mount: the effect cleanup revoked the URL
 * during the strict unmount, but consumers still saw the (now-dead) URL on
 * the remount.
 */
export function useAttachmentBlob(
  siteId: string,
  issueId: string,
  attachmentId: string | null,
): { url: string | null; isLoading: boolean; isError: boolean } {
  const query = useQuery<Blob, Error>({
    queryKey: ['monitor', 'issue-attachment', siteId, issueId, attachmentId],
    queryFn: () => fetchAttachmentBlob(siteId, issueId, attachmentId!),
    enabled: !!attachmentId,
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
  });

  const [url, setUrl] = useState<string | null>(null);

  // Side-effectful resources (blob URLs) must be created+revoked in the same
  // effect so they're tied to a single mount cycle. There's no equivalent in
  // render — hence the rule disable.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!query.data) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(query.data);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [query.data]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return {
    url,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
